import { ActionSelectMenu, ControlledCheckbox } from '@act/storybook';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { membersRequests } from 'redux/requests/members.request';
import { useAppSelector } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import styled from 'styled-components';
import { hasPermission } from 'utilities/hasPermissions';
import { SearchBar, SelectOption } from '../../components/search-bar';
import { testNumberString } from '../calls-texts-utils';
import { BaseFilterConfig, PaginatedFilterConfig } from './types';
import { Button } from '@act/storybook';

const FilterContainer = styled.div`
  margin: 16px;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 20px;

  .filters-button {
    margin-left: auto;
  }
`;

const ButtonPanel = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

interface CallsFiltersProps {
  control: Control<PaginatedFilterConfig>;
  activeFilterCount: number;
  setValue: UseFormSetValue<PaginatedFilterConfig>;
  getValues: UseFormGetValues<PaginatedFilterConfig>;
  handleOpenFilters: () => void;
  handleFiltering: () => void;
  currentFilterConfig: BaseFilterConfig;
  disabled?: boolean;
}

const CallFilters = ({
  control,
  activeFilterCount,
  setValue,
  getValues,
  handleOpenFilters,
  handleFiltering,
  currentFilterConfig,
  disabled,
}: CallsFiltersProps) => {
  const [typeaheadOptions, setTypeaheadOptions] = useState<SelectOption[]>([]);
  const [typeaheadUsers, isLoadingTypeahead, getTypeaheadUsers] = useApiCall(
    membersRequests.getPaginatedMembersForTable
  );

  const actingUser = useAppSelector(state => state.actingUser)?.user;

  useEffect(() => {
    if (typeaheadUsers?.users) {
      const options = typeaheadUsers.users.map(u => {
        return {
          value: u.id,
          label: `${u.firstName} ${u.lastName}`,
        };
      });
      setTypeaheadOptions(options);
    } else {
      setTypeaheadOptions([]);
    }
  }, [typeaheadUsers]);

  function setTypeaheadSearchTerm(searchTerm: string) {
    const overrideFilters = {
      searchTerm,
      'page-start': 0,
      'page-size': 10,
      includeCount: true,
      orderBy: null,
      orderDirection: null,
      includeDetails: true,
    };
    const filterConfig = {
      ...currentFilterConfig,
      ...overrideFilters,
    };
    getTypeaheadUsers({ params: filterConfig });
  }

  return (
    <FilterContainer>
      <div>
        <Controller
          control={control}
          name="searchTerm"
          disabled={disabled}
          render={({ field: { onChange, value, disabled } }) => {
            return (
              <SearchBar
                value={value}
                disabled={disabled}
                label="Search by member name or phone number"
                options={typeaheadOptions}
                isLoading={isLoadingTypeahead}
                setTypeaheadSearchTerm={setTypeaheadSearchTerm}
                setSearchTerm={(searchTerm: string | SelectOption) => {
                  setValue('searchTermType', null);
                  setValue('searchTerm', null);
                  if (typeof searchTerm === 'string') {
                    // check to see if searching a phone number or name
                    if (testNumberString(searchTerm)) {
                      setValue('searchTermType', 'number');
                    } else {
                      setValue('searchTermType', 'string');
                    }
                    setValue('searchTerm', searchTerm);
                    onChange(searchTerm);
                  } else {
                    if (searchTerm?.label) {
                      onChange(searchTerm?.label);
                      setValue('subjectIds', searchTerm?.value);
                      setValue('searchTermType', 'subjectId');
                    } else {
                      onChange(null);
                      setValue('subjectIds', null);
                      setValue('searchTermType', null);
                    }
                  }
                  handleFiltering();
                }}
              />
            );
          }}
        ></Controller>
      </div>
      <OptionsContainer>
        <ControlledCheckbox
          ariaLabel="Only unassigned"
          control={control}
          defaultChecked={false}
          label="Only unassigned"
          onChange={handleFiltering}
          name="onlyUnassignedCalls"
          disabled={disabled}
        />

        {hasPermission(actingUser, 'CAN_MANAGE_MEMBER_FEEDBACK') && (
          <ControlledCheckbox
            ariaLabel="Only feedback calls"
            control={control}
            defaultChecked={false}
            onChange={handleFiltering}
            label="Only feedback calls"
            name="onlyFeedbackCalls"
          />
        )}

        <ControlledCheckbox
          ariaLabel="Only my calls"
          control={control}
          defaultChecked={false}
          onChange={handleFiltering}
          label="Only my calls"
          name="showOnlyMyCalls"
        />

        <ButtonPanel className="filters-button">
          <ActionSelectMenu
            menuIcon="calendar_month"
            actions={[
              {
                display: 'Last 30 Days',
                defaultSelected: getValues('lastDaysFromNow') === 30,
                callback: () => {
                  setValue('lastDaysFromNow', 30);
                  handleFiltering();
                },
              },
              {
                display: 'Last 60 Days',
                defaultSelected: getValues('lastDaysFromNow') === 60,
                callback: () => {
                  setValue('lastDaysFromNow', 60);
                  handleFiltering();
                },
              },
              {
                display: 'Last 90 Days',
                defaultSelected: getValues('lastDaysFromNow') === 90,
                callback: () => {
                  setValue('lastDaysFromNow', 90);
                  handleFiltering();
                },
              },
              {
                display: 'All',
                defaultSelected: !getValues('lastDaysFromNow'),
                callback: () => {
                  setValue('lastDaysFromNow', null);
                  handleFiltering();
                },
              },
            ]}
          />
          <Button
            startIcon={<FilterListIcon />}
            variant="outlined"
            onClick={handleOpenFilters}
            disabled={disabled}
            debounced
          >
            <Typography variant="labelLarge">
              Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
            </Typography>
          </Button>
        </ButtonPanel>
      </OptionsContainer>
    </FilterContainer>
  );
};

export default CallFilters;
